import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openChatbot } from 'shared/actions/chat';
import { ExpertOpinionBanner } from 'shared/components/ExpertOpinionBanner/ExpertOpinionBanner';
import { useExpertOptionEnabled } from 'shared/hooks/useExpertOptionEnabled';
import { IExpertOfferSuggestionsSchema } from 'shared/repositories/cian-chat-bot/entities/expert_opinion_offer/ExpertOfferSuggestionsSchema';
import { selectOfferId } from 'shared/selectors';
import { getOfferSuggestions } from 'shared/services/getOfferSuggestions';

import {
  trackClickExpertOptionButton,
  trackClickExpertOptionInput,
  trackSendExpertOptionInput,
  trackShowExpertOption,
} from './tracking';
import { useApplicationContext } from '../ApplicationContext';

export const ExpertOpinionBannerContainer = () => {
  const dispatch = useDispatch();
  const expertOpinionButtonEnabled = useExpertOptionEnabled();
  const [suggestions, setSuggestions] = React.useState<IExpertOfferSuggestionsSchema[]>([]);
  const [textMessage, setTextMessage] = React.useState<string>('');

  const offerId = useSelector(selectOfferId);
  const context = useApplicationContext();

  React.useEffect(() => {
    const fetchSuggestionsAndTrack = async () => {
      const suggestions = await getOfferSuggestions(context, { offerId });
      setSuggestions(suggestions);

      if (suggestions.length > 0 && expertOpinionButtonEnabled) {
        trackShowExpertOption({ offerId, suggestions });
      }
    };

    fetchSuggestionsAndTrack();
  }, [offerId, context, expertOpinionButtonEnabled]);

  const handleClickExpertButton = React.useCallback(
    (defaultMessage: string) => {
      trackSendExpertOptionInput(offerId, defaultMessage);

      dispatch(openChatbot({ defaultMessage }));
    },
    [dispatch],
  );

  const handleSuggestionClick = React.useCallback(
    (suggestion: IExpertOfferSuggestionsSchema) => {
      const { sendText, displayText } = suggestion;
      trackClickExpertOptionButton(offerId, displayText);

      dispatch(openChatbot({ defaultMessage: sendText }));
    },
    [dispatch],
  );

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTextMessage(e.target.value);
  }, []);

  const handleClickExpertInput = () => {
    trackClickExpertOptionInput(offerId);
  };

  if (!expertOpinionButtonEnabled) {
    return null;
  }

  return (
    <ExpertOpinionBanner
      suggestions={suggestions}
      textMessage={textMessage}
      onInputChange={handleInputChange}
      onClick={handleClickExpertButton}
      onSuggestClick={handleSuggestionClick}
      onInputClick={handleClickExpertInput}
    />
  );
};
